import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CATEGORY_MENU_DATA_URL } from 'aster-foundation/core/common/utils/ApiConst';
import { InputGroup, FormControl, Dropdown, FloatingLabel } from 'react-bootstrap';
import Image from 'next/image';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { IC_DROPDOWN_ARROW, IC_DROPDOWN_ARROW_WHITE } from '../../Assets/images';
import CountryCodeInputStyled from './CountryCodeInputStyled';
import { validateUAEMobileNumber, validateIndiaMobileNumber, getIsInstoreApp, getHostCountry } from '@common/Utility/common-utils';
import { getLangCodeFromUrl } from '../../../../i18n';
import { isRTL } from '@common/i18n';

const CountryCodeInput = ({ countryData, setMobileNumber, errorMsg, showLabel, placeholder, showFailureModal, mobileNumber, otpTimer, enableGuestCheckout, darkMode }) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  const [selectedCountry, setSelectedCountry] = useState(
    countryData?.filter((item) => item.shortForm === getHostCountry())?.[0] || countryData[0],
  );

  const [number, setNumber] = useState(enableGuestCheckout ? mobileNumber : '');
  const [maxLength, setMaxLength] = useState(selectedCountry?.numberLength || 20);
  const [currentLang, setCurrentLanguage] = useState('');

  useEffect(() => {
    if(typeof window !== 'undefined'){
      setCurrentLanguage(getLangCodeFromUrl());
    }
  }, []);

  const isValidNumber = (countryCode, inputNumber) => {
    try {
      let handledNumber = inputNumber;
      if (handledNumber.startsWith('0')) {
        handledNumber = handledNumber.substring(1, handledNumber.length);
      }

      switch (countryCode) {
        case '+971':
        case '+966':
          return validateUAEMobileNumber(handledNumber);
        case '+91':
          return validateIndiaMobileNumber(handledNumber);
        default:
          return phoneUtil.parse(countryCode + handledNumber);
      }
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    if (enableGuestCheckout && showFailureModal) {
      const number = JSON.parse(localStorage.getItem('guestMobile'));
      setNumber(number);
    } else if(getIsInstoreApp() && mobileNumber) {
      setNumber(mobileNumber);
    }
  }, [showFailureModal, enableGuestCheckout]);

  const updateMobileNumber = (enteredCountry, enteredNumber) => {
    let handledNumber = enteredNumber;
    if (handledNumber.startsWith('0')) {
      handledNumber = handledNumber.substring(1, handledNumber.length);
    }
    const isNumberValid = isValidNumber(enteredCountry?.countryCode, handledNumber);
    setMobileNumber({ countryCode: enteredCountry?.countryCode, number: handledNumber, isNumberValid });
  };

  const handleCountrySelect = (inputCountry) => {
    setSelectedCountry(inputCountry);
    updateMobileNumber(inputCountry, number);
    if (inputCountry?.shortForm === 'uae' && number.startsWith('0') && number.length > 0) {
      setMaxLength(inputCountry.numberLength + 1);
    } else {
      setMaxLength(inputCountry?.numberLength);
    }
  };

  const handleInput = (e) => {
    e.target.value = e.target.value.slice(0, maxLength);

    if (selectedCountry?.shortForm === 'uae') {
      if (e.target.value.startsWith('0') && e.target.value.length === 1) {
        setMaxLength(selectedCountry.numberLength + 1);
      } else if (!e.target.value.startsWith('0') && e.target.value.length === 1) {
        setMaxLength(selectedCountry?.numberLength);
      }
    }
  };

  const handleOnChange = (object) => {
    setNumber(object.target.value);
    updateMobileNumber(selectedCountry, object.target.value);
  };

  return (
    <CountryCodeInputStyled darkMode={darkMode} className={`${currentLang && isRTL(currentLang) ? 'rtl' : ''}`}>
      <>
        <InputGroup className="input-group">
          <div className="country-div">
            <div className="dropdown-border">
              <Dropdown as={InputGroup.Prepend}>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  id="country-dropdown-toggle"
                  className="country-dropdown-toggle"
                  disabled={showFailureModal || otpTimer?.length > 0}
                >
                  {selectedCountry ? (
                    <span className="mt-10">
                      <Image
                        src={`${CATEGORY_MENU_DATA_URL}${selectedCountry.flagPath}`}
                        width={24}
                        height={16}
                        alt="Aster"
                        priority
                      />
                      <span className="country-dropdown-code">{`${selectedCountry.countryCode}`}</span>
                    </span>
                  ) : (
                    'Country'
                  )}
                  <span className='country-dropdown-arrow'>
                    <Image src={ darkMode ? IC_DROPDOWN_ARROW_WHITE : IC_DROPDOWN_ARROW } alt="Aster" priority />
                  </span>
                </Dropdown.Toggle>
                {countryData?.length > 1 && (
                  <Dropdown.Menu className="w-100 country-scrollbar">
                    {countryData &&
                      countryData
                        ?.filter((x) => x.show === true)
                        ?.slice(0, -2)
                        ?.map((country, i) => (
                          <Dropdown.Item
                            key={i}
                            eventKey={country?.countryCode}
                            onClick={() => handleCountrySelect(country)}
                            disabled={enableGuestCheckout ? (showFailureModal || otpTimer?.length > 0) : false}
                          >
                            <div className="country-dropdown-item">
                              <Image
                                src={`${CATEGORY_MENU_DATA_URL}${country.flagPath}`}
                                width={24}
                                height={16}
                                alt="Aster"
                                loading="lazy"
                              />
                              <span 
                              style={{ 
                                fontSize: '15px', 
                                paddingLeft: currentLang && isRTL(currentLang) ? '0' : '8px',
                                paddingRight: currentLang && isRTL(currentLang) ? '8px' : '0', 
                              }}
                              >
                                <span className="country-name"> {country?.[currentLang] || country.countryName}</span>
                                <span className="country-code"> ({country.countryCode})</span>
                              </span>
                            </div>
                          </Dropdown.Item>
                        ))}
                  </Dropdown.Menu>
                )}
              </Dropdown>
            </div>
            <div>
              <FloatingLabel label={showLabel && placeholder} controlId="mobileNumber">
                <FormControl
                  aria-describedby="country-code"
                  className="country-dropdown-textbox shadow-none"
                  onChange={handleOnChange}
                  maxLength={maxLength}
                  placeholder="Mobile Number"
                  height="20px"
                  onInput={handleInput}
                  autoComplete="off"
                  type="number"
                  value={number}
                  disabled={showFailureModal || otpTimer?.length > 0}
                />
              </FloatingLabel>
            </div>
          </div>
        </InputGroup>
        {errorMsg && <div className="invalid-feedback d-block invalid-error">{errorMsg}</div>}
      </>
    </CountryCodeInputStyled>
  );
};

CountryCodeInput.defaultProps = {
  showLabel: true,
  placeholder: '',
  showFailureModal: false,
  mobileNumber: '',
  enableGuestCheckout: false,
  darkMode: false
};

CountryCodeInput.propTypes = {
  countryData: PropTypes.array.isRequired,
  setMobileNumber: PropTypes.func.isRequired,
  errorMsg: PropTypes.string.isRequired,
  showLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  showFailureModal: PropTypes.bool,
  mobileNumber: PropTypes.string,
  enableGuestCheckout: PropTypes.bool,
  darkMode: PropTypes.bool
};

export default CountryCodeInput;
